/**
 * Account HTTP client for Settings > Profile (name / picture edits and the
 * Danger Zone "Delete Account" flow).
 *
 * Every call resolves `{ ok, data | error, status }` like the workspace
 * client so the components `await` without try/catch.  Backend routes live in
 * `backend/app/routes/users_route.py` (`POST /users/set-profile`,
 * `GET /users/me/deletion-preview`, `DELETE /users/me`).
 */
import { sendJson, updateProfile as updateProfileFields } from '@/features/workspaces/api/workspacesApi';

/**
 * What deleting the account would do to the workspaces the caller owns
 * (`GET /users/me/deletion-preview`).  Resolves
 * `{ ok: true, data: { owned_workspaces: [{ id, name, member_count, has_other_admin, requires_transfer }] } }`.
 * `requires_transfer` marks multi-user workspaces with no other Admin: the
 * owner has to hand those over before the account can go.
 */
export const getDeletionPreview = () => sendJson('GET', 'users/me/deletion-preview');

/**
 * Delete the caller's account (`DELETE /users/me`): solo workspaces are
 * deleted, owned team workspaces are handed to another Admin, memberships
 * are removed and the session cookie is cleared.  Resolves
 * `{ ok: true, data: { deleted: true } }`; a 409 means a workspace still
 * needs an explicit ownership transfer.
 */
export const deleteAccount = () => sendJson('DELETE', 'users/me');

/**
 * Save profile fields (`POST /users/set-profile`).  Settings > Profile passes
 * `{ name }` or `{ picture }` (a square data URL); the backend flags both as
 * user-edited so the next login no longer overwrites them from the identity
 * provider.  Resolves `{ ok: true, data: { success, user } }`.
 */
export const updateProfile = (fields) => updateProfileFields(fields);
